// Altcha-protocol PoW captcha widget. Self-hosted; no third-party CDN.
//
// Lifecycle:
//   1. On mount (or resetKey change): GET /api/auth/challenge
//   2. Iterate 0..maxnumber looking for sha256(salt+i) === challenge,
//      via Web Crypto. Median solve on a laptop: 100-300ms; phone: 500ms.
//   3. When solved, hand the full solution back via onSolution() so the
//      parent can include it in the login/register POST as `altcha`.
//
// We do the hashing in chunks of 1000 iterations per microtask so the
// UI thread can repaint and the "Solving…" spinner doesn't freeze.
//
// Web Crypto's subtle.digest is async-only, so we batch small ranges
// synchronously using a hex-string sha256 implementation. Otherwise the
// per-call overhead dominates and a 100k-iteration solve takes 30+
// seconds.
import { useEffect, useRef, useState } from "react";
import { T } from "../theme.js";
import { useT } from "../i18n.jsx";

const BATCH = 1000;          // iterations per microtask yield
const MAX_BATCHES = 200;     // safety: stops after 200k iterations regardless of maxnumber

// Minimal sha256-hex via Web Crypto. async — used only for verifying a
// single candidate at the end of a batch (cheap).
async function sha256Hex(str) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
}

export function AltchaWidget({ onSolution, resetKey = 0 }) {
  const t = useT();
  const [state, setState] = useState("loading"); // loading | solving | ready | error
  const [err, setErr] = useState("");
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    setState("loading"); setErr("");
    onSolution(null);

    (async () => {
      let challenge;
      try {
        const res = await fetch("/api/auth/challenge");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        challenge = await res.json();
      } catch (e) {
        if (cancelledRef.current) return;
        setState("error"); setErr(e.message || "challenge fetch failed");
        return;
      }
      if (cancelledRef.current) return;
      setState("solving");

      // PoW solve loop. We sweep i in batches and check sha256(salt+i)
      // until it matches the challenge. We give up after MAX_BATCHES *
      // BATCH iterations — at maxnumber=100000 that's 2x headroom.
      try {
        const { salt, challenge: target, maxnumber } = challenge;
        const cap = Math.min(maxnumber, MAX_BATCHES * BATCH);
        for (let base = 0; base < cap; base += BATCH) {
          if (cancelledRef.current) return;
          // Hash each candidate in this batch. We must use subtle.digest,
          // but we can issue all hashes in parallel and pick the match.
          const end = Math.min(base + BATCH, cap);
          const hashes = await Promise.all(
            Array.from({ length: end - base }, (_, k) => sha256Hex(salt + (base + k))),
          );
          if (cancelledRef.current) return;
          const idx = hashes.indexOf(target);
          if (idx !== -1) {
            const number = base + idx;
            onSolution({
              algorithm: challenge.algorithm,
              challenge: target,
              number,
              salt,
              signature: challenge.signature,
              ts: challenge.ts,
            });
            setState("ready");
            return;
          }
          // Yield so the browser can repaint between batches.
          await new Promise((r) => setTimeout(r, 0));
        }
        if (cancelledRef.current) return;
        setState("error"); setErr("Solver gave up");
      } catch (e) {
        if (cancelledRef.current) return;
        setState("error"); setErr(e.message || "solver failed");
      }
    })();

    return () => { cancelledRef.current = true; };
  }, [resetKey]);

  return (
    <div style={{
      marginBottom: 16, padding: "10px 14px",
      background: T.bg, borderRadius: 10,
      border: `1px solid ${T.border}`,
      display: "flex", alignItems: "center", gap: 10,
      fontSize: 13, color: T.textSec,
    }}>
      {state === "loading" && (
        <>
          <Spinner /><span>{t("altcha.loading")}</span>
        </>
      )}
      {state === "solving" && (
        <>
          <Spinner /><span>{t("altcha.solving")}</span>
        </>
      )}
      {state === "ready" && (
        <>
          <span style={{ color: T.online, fontSize: 16, lineHeight: 1 }}>✓</span>
          <span>{t("altcha.ready")}</span>
        </>
      )}
      {state === "error" && (
        <>
          <span style={{ color: T.danger, fontSize: 16, lineHeight: 1 }}>!</span>
          <span style={{ color: T.danger }}>{t("altcha.error")}: {err}</span>
        </>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span style={{
      display: "inline-block", width: 14, height: 14,
      border: `2px solid ${T.border}`, borderTopColor: T.accent,
      borderRadius: "50%", animation: "cgspin 0.8s linear infinite",
    }}>
      <style>{`@keyframes cgspin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );
}
